
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Helmet } from 'react-helmet-async';
import { Separator } from "@/components/ui/separator";
import ArticleContent from '@/components/ratgeber/ArticleContent';
import ArticleCard from '@/components/ratgeber/ArticleCard';
import { articles } from '@/data/ratgeberArticles';
import { TableOfContents } from '@/components/TableOfContents';
import { useLanguage } from '@/contexts/LanguageContext';

const Ratgeber = () => {
  const location = useLocation();
  const [activeHash, setActiveHash] = useState('');
  const { t } = useLanguage();

  // Update active hash when URL hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setActiveHash(hash);
      // Scroll to the article with a small delay for smooth experience
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else if (articles.length > 0) {
      // Default to first article if no hash
      setActiveHash(articles[0].id);
    }
  }, [location.hash]);

  // Generate table of contents items
  const tocItems = articles.map(article => ({
    id: article.id,
    title: t(article.title)
  }));

  return (
    <>
      <Helmet>
        <title>{t('dogGuideTitle')} | {t('dogGuideMetaTitle')}</title>
        <meta name="description" content={t('dogGuideMetaDescription')} />
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-6"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-6">{t('dogGuide')}</h1>
          <p className="text-lg text-gray-700 mb-8">
            {t('dogGuideIntro')}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar with article links */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <TableOfContents items={tocItems} title={t('topics')} defaultOpen={true} />
              </div>
            </div>

            {/* Main content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                {articles.map(article => (
                  <section 
                    key={article.id} 
                    id={article.id} 
                    className={`mb-12 ${activeHash === article.id ? 'scroll-mt-24' : ''}`}
                  >
                    <h2 className="text-2xl font-bold text-primary mb-4">{t(article.title)}</h2>
                    <Separator className="mb-6" />
                    
                    <ArticleContent content={article.content} />
                    
                    <div className="mt-6 flex justify-end">
                      <a href="#" className="text-primary hover:underline text-sm">
                        {t('toTop')} ↑
                      </a>
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer />
    </>
  );
};

export default Ratgeber;
